import React, { useEffect, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { usePagesContent } from '../../../../Hooks/usePagesContent';
import AppCache from '../../../../Helper/Common/AppCache';
import { fetchFiltersDataObject } from '../../../../Helper/Table/TableHelper';
import { getDigitalTwinAlerts } from '../../../../Api/DigitalTwin';
import { convertZonetoUTCEnd, convertZonetoUTCStart, getDatePickerDate } from '../../../../Helper/DatePicker/DateConverter';
import SingleAlertAnalysis from '../../../../Features/AnalyticsDrillDown/SingleChartAnalysis/SingleAlertAnalysis';
import { getDigitalTwinData } from '../../../../Api/DigitalTwinListApi';
import { useBatteryInfo } from '../../../../Pages/DigitalTwinV3/DigitalTwinContext';
import { getEffectedAlertEntity } from '../../../../Helper/DigitalTwin/DigitalTwinFunctions';
import { decryptTheParams } from '../../../../Helper/QueryParams/EncryptDecrypt';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import { MAX_COLUMN_SELECTION_COUNT } from '../../../../Helper/DigitalTwin/DataFile';

const TableContainer = styled(Box)(({ theme }) => ({
    minHeight: '200px',
}))

const customRenderers = {
    'single_alert_analysis': (record, config) => {
        return <SingleAlertAnalysis alertStateId={record.id} />
    },

    'effectedAlertEntity': (record, config) => {
        return <Typography variant="fw400s14lh24c3D4B5A">{getEffectedAlertEntity(record.hierarchy, record.affectedEntity, record.affectedEntity, record.affectedEntity)}</Typography>
    }
}


const TABLE_ID = 'BatteryAlertLogs'

const defaultStartDate = convertZonetoUTCStart(getDatePickerDate(undefined, -90))
const defaultEndDate = convertZonetoUTCEnd(getDatePickerDate(undefined, 0))

const getDefaultFilters = (queryParams) => {
    try {
        let queryFilters = queryParams?.alertLogFilters || '[]'
        queryFilters = JSON.parse(queryFilters)
        if (queryFilters && Object.keys(queryFilters).length > 0) {
            return queryFilters
        }
    } catch (error) {
        console.log({ error })
    }
    return {}
}

const BatteryAlertLogs = ({ batteryID }) => {
    const { t } = useTranslation(['common', 'generic_table'])
    const { batteryInfo } = useBatteryInfo()
    const pageContent = usePagesContent()
    const [selectedColumns, setSelectedColumns] = useState([])
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([])
    const [columnOrder, setColumnOrder] = useState([]);
    const [pageNumber, setPageNumber] = useState(1)
    const [isLoading, setIsLoading] = useState(true)
    const [availableColumns, setAvailableColumns] = useState([])
    const [currentTableColumns, setCurrentTableColumns] = useState([])
    const [availableFilters, setAvailableFilters] = useState({})
    const [selectedFilters, setSelectedFilters] = useState(getDefaultFilters(decryptTheParams()))
    const [selectedRowKeys, setSelectedRowKeys] = useState({});
    const [tableData, setTableData] = useState([]);
    const [pageInfo, setPageInfo] = useState({});
    const [sorting, setSorting] = useState([
        { "sortBy": "alertStatus", "order": "asc" },
        { "sortBy": "severityLevelID", "order": "desc" }
    ]);
    const pageCount = 6


    const fetchDigitalTwinAlerts = async () => {
        try {
            console.log("fetching alerts", { isLoading })
            setIsLoading(true)
            const defaultFilters = {
                "batteryID": [batteryID]
            }

            const activatedAtFilter = selectedFilters.activatedAt ? JSON.parse(selectedFilters.activatedAt) : {}

            const startDate = activatedAtFilter.startDateUTC || defaultStartDate
            const endDate = activatedAtFilter.endDateUTC || defaultEndDate

            const reqParams = {
                "startDate": startDate,
                "endDate": endDate,
                "pageCount": pageCount,
                "sortBy": JSON.stringify(sorting),
                "filters": JSON.stringify(
                    {
                        ...selectedFilters,
                        ...defaultFilters
                    }
                )
            }
            const responseData = await getDigitalTwinAlerts(reqParams, pageNumber)

            const data = responseData.response.data || []
            const pageInfo = responseData.response.total || {}
            setPageInfo(pageInfo)
            setTableData(data)
            setIsLoading(false)
        } catch (error) {
            console.log(error)
            setIsLoading(false)
        }
    }

    const applySelectedColumns = (newSelectedColumns) => {
        AppCache.setItem(TABLE_ID, { selectedColumns: newSelectedColumns })
        setSelectedColumns(newSelectedColumns)
    }

    const applySelectedFilters = (filters) => {
        return setSelectedFilters(filters)
    }

    const onPageChange = (pageNo) => {
        setPageNumber(pageNo)
    }

    const getTotalRowSelection = () => {
        return Object.values(selectedRowKeys).flat()
    }

    const getCurrentPageSelection = () => {
        if (!selectedRowKeys) {
            return []
        }

        if (!selectedRowKeys[pageNumber]) {
            return []
        }

        return selectedRowKeys[pageNumber]
    }

    const onSelectChange = (selectedKeys) => {
        const allSelectedRows = { ...selectedRowKeys }
        allSelectedRows[pageNumber] = selectedKeys
        setSelectedRowKeys(allSelectedRows);
    }

    const applySort = (key, order) => {
        const sortDetails = { sortBy: key, order: order }
        setSorting(sortDetails)
    };

    const formatColumnConfigsForCSV = (coulmnConfigs) => {
        return coulmnConfigs.map((cConfig) => {
            if (cConfig.dataIndex.toLowerCase() == 'resolutiontime') {
                return { ...cConfig, headerLabel: 'Resolution Time (Minutes)' }
            }

            return { ...cConfig }
        })
    }

    const getCsvColumnParams = () => {
        const csvColumnConfigs = formatColumnConfigsForCSV(currentTableColumns)
        return {
            columnOrder: csvColumnConfigs.map(column => column.headerLabel),
            columns: JSON.stringify(csvColumnConfigs)
        }
    }

    const defaultActionReqParams = () => {
        return {
            resourceIDs: getTotalRowSelection(),
            resourceType: 'alert-list',
            actionType: "download-csv",
            ...getCsvColumnParams()
        }
    }

    const getCSVFileName = () => {
        return `BatteryAlertLogs_${batteryInfo.deviceID}_${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.csv`
    }

    const getCSVData = async () => {
        const reqParams = {
            ...defaultActionReqParams(),
            actionType: "download-csv",
        }

        const res = await getDigitalTwinData(reqParams)

        if (res && res.responseStatus.code === 200) {
            return res.response.data
        }

        return []
    }

    const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
        const reqParams = {
            ...defaultActionReqParams(),
            actionType: "mail",
            emailTo: sendTo,
            emailCc: sendCc,
            subject: subject,
            body: body,
            fileName: getCSVFileName()
        }

        const res = await getDigitalTwinData(reqParams)
        if (res && res.responseStatus.code === 200) {
            return res.response.data
        }

        return []
    }

    const setupTableColumns = () => {
        const tableInfo = pageContent?.metaInfo?.table?.alertLogTable || {}
        const tableColumns = tableInfo?.columnsConfig || {}
        const tableColumnOrder = tableInfo?.columnOrdering || []
        const availableColumns = getColumnsConfig({
            tableColumns: tableColumns,
            tableColumnOrder: tableColumnOrder,
            customRenderers,
            t
        })

        setAvailableColumns(availableColumns)

        const initalSelectedColumns = AppCache.getItem(TABLE_ID)?.selectedColumns || Object.keys(tableColumns) || []

        // sort by fixed key and then pick the first MAX_COLUMN_SELECTION_COUNT columns
        const defaultColumns = initalSelectedColumns.sort((a, b) => {
            if (a.fixed && !b.fixed) return -1;
            if (!a.fixed && b.fixed) return 1;
            return 0;
        }).slice(0, MAX_COLUMN_SELECTION_COUNT);
        setSelectedColumns(defaultColumns)
        setDefaultSelectedColumns(defaultColumns)

        const currentTableColumns = availableColumns.filter(column => initalSelectedColumns.includes(column.key))
        setCurrentTableColumns(currentTableColumns)
        setColumnOrder(currentTableColumns.map(column => column.headerLabel))
    }

    const setupTableFilters = async () => {
        const tableFilters = pageContent?.metaInfo?.filterConfigurations?.alertLogTable?.filters || []
        const filtersObj = await fetchFiltersDataObject(tableFilters, availableFilters)
        const sortedFiltersObj = Object.fromEntries(
            Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
        );
        setAvailableFilters(sortedFiltersObj)
    }

    const onColumnOrderChange = (tableColumns) => {
        const updatedColumnOrder = tableColumns.map((value) => value.headerLabel)
        setColumnOrder(updatedColumnOrder);
    }

    const getCountText = () => {
        if (!pageInfo.total) {
            return ''
        }

        if (pageInfo.total === 1) {
            return '1 log'
        }

        return pageInfo.total + ' logs'
    }

    const getFilteredCount = () => {

        if (!selectedFilters || Object.keys(selectedFilters) < 1) {
            return false
        }

        return pageInfo.total ? pageInfo.total : 0
    }

    const getNoDataMessage = () => {
        if (!selectedFilters || Object.keys(selectedFilters) < 1) {
            return "No issues found in this battery"
        }

        return "No matches found for filters applied"
    }

    useEffect(() => {
        setupTableColumns()
        setupTableFilters()
    }, [pageContent])


    useEffect(() => {
        const currentTableColumns = availableColumns.filter(column => selectedColumns.includes(column.key))
        setCurrentTableColumns(currentTableColumns)
    }, [selectedColumns])


    useEffect(() => {
        pageNumber === 1 ? fetchDigitalTwinAlerts() : onPageChange(1)
    }, [selectedFilters, sorting])


    useEffect(() => {
        fetchDigitalTwinAlerts();
    }, [pageNumber]);


    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={'BatteryAlertLogs'}
                title={'Alert and Warning Log'}
                count={getCountText()}
                filteredCount={getFilteredCount()}
                rowsPerPage={pageCount}
                showPrimaryButton={false}
                disableSearch={true}
                getCSVData={getCSVData}
                getCSVFileName={getCSVFileName}
                onSendEmail={onSendEmail}
                availableEmailDomains={pageContent?.metaInfo?.availableEmailDomains || ['coulomb.ai']}
                filters={availableFilters}
                selectedFilters={selectedFilters}
                applyFilters={(args) => applySelectedFilters(args)}
                tableColumns={currentTableColumns}
                initialColumns={availableColumns}
                selectedColumns={selectedColumns}
                applySelectedColumns={applySelectedColumns}
                tableData={tableData}
                onColumnOrderChange={onColumnOrderChange}
                columnOrder={columnOrder}
                getCsvColumnSettings={getCsvColumnParams}
                isLoading={isLoading}
                pageNumber={pageNumber}
                total={pageInfo.total}
                applySort={applySort}
                onPageChange={onPageChange}
                emptyMessage={getNoDataMessage()}
                setSelectedRowKeys={setSelectedRowKeys}
                getTotalRowSelection={getTotalRowSelection}
                rowSelection={{
                    selectedRowKeys: getCurrentPageSelection(),
                    onChange: onSelectChange,
                }}
                emailInfo={{
                    subject: `⚠️ Attention! View Alert Details for ${batteryInfo.deviceID} `,
                    body: `CSV document containing data of ${batteryInfo.deviceID} attached herewith:`
                }}
                maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                defaultSelectedColumns={defaultSelectedColumns}
            />
        </TableContainer>
    )
};


export default BatteryAlertLogs;
